import analyticsMenu from '../config/analyticsMenu'
import notesMenu from '../config/notesMenu'
import purchasesMenu from '../config/purchasesMenu'
import settingsMenu from '../config/settingsMenu'
import utilityBillsMenu from '../config/utilityBillsMenu'
import { appModulesForLanding, getIncomeSectionModule, getPurchaseSectionModule } from '../config/appModules'
import { AuthUser } from '../domains/Login/types/users'
import {
  ModulePermission,
  isPrivateModeModuleHidden,
  normalizeUserModules,
} from '../domains/Settings/config/userModules'
import { MenuItem } from '../types'

export const getModulePermission = (
  user: AuthUser | undefined,
  code: string,
): ModulePermission => {
  if (!user) {
    return 'hide'
  }

  if (
    isPrivateModeModuleHidden(user.isPrivateModeActive, user.modules, code)
  ) {
    return 'hide'
  }

  if (user.role === 'admin') {
    return 'edit'
  }

  const matchedModule = normalizeUserModules(user.modules).find(
    (moduleItem) => moduleItem.code === code,
  )

  return matchedModule?.permission || 'hide'
}

export const canViewModule = (
  user: AuthUser | undefined,
  code: string,
): boolean => {
  const permission = getModulePermission(user, code)
  return permission === 'view' || permission === 'edit'
}

export const canEditModule = (
  user: AuthUser | undefined,
  code: string,
): boolean => {
  return getModulePermission(user, code) === 'edit'
}

export const getVisibleNoteMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  return notesMenu.filter((item) => canViewModule(user, item.code))
}

export const getDefaultNotesRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleNoteMenuItems(user)[0]?.path
}

export const getVisiblePurchaseMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  if (!canViewModule(user, 'purchases')) {
    return []
  }

  return purchasesMenu
}

export const getDefaultPurchasesRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisiblePurchaseMenuItems(user)[0]?.path
}

export const getVisibleUtilityBillsMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  if (!canViewModule(user, 'utility-bills')) {
    return []
  }

  return utilityBillsMenu
}

export const getDefaultUtilityBillsRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleUtilityBillsMenuItems(user)[0]?.path
}

export const getVisibleSettingsMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  if (!canViewModule(user, 'settings')) {
    return []
  }

  return settingsMenu
}

export const getDefaultSettingsRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleSettingsMenuItems(user)[0]?.path
}

export const getVisibleAnalyticsMenuItems = (
  user: AuthUser | undefined,
): MenuItem[] => {
  return analyticsMenu.filter((item) => canViewModule(user, item.code))
}

export const getDefaultAnalyticsRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  return getVisibleAnalyticsMenuItems(user)[0]?.path
}

export const getDefaultPurchaseSectionRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  if (canViewModule(user, 'purchases')) {
    return '/purchases?tab=daily'
  }

  if (canViewModule(user, 'utility-bills')) {
    return getDefaultUtilityBillsRoute(user)
  }

  if (canViewModule(user, 'spend-analytics')) {
    return '/purchases?tab=analytics'
  }

  return undefined
}

export const getDefaultIncomeSectionRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  if (canViewModule(user, 'income')) {
    return '/income'
  }

  if (canViewModule(user, 'income-analytics')) {
    return '/income?tab=analytics'
  }

  return undefined
}

export { getIncomeSectionModule, getPurchaseSectionModule }

export const getDefaultAuthorizedRoute = (
  user: AuthUser | undefined,
): string | undefined => {
  if (!user) {
    return undefined
  }

  if (user.role === 'admin') {
    return '/'
  }

  return appModulesForLanding.find((route) => canViewModule(user, route.code))
    ?.path
}
